import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Phone,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import authService from "@/services/auth.service";
import { isHeyApiError } from "@/lib/hey-api-client";

const getRegisterErrorDescription = (error: unknown): string => {
  if (isHeyApiError(error)) {
    if (typeof error.detail === "string") {
      return error.detail;
    }
    if (
      error.detail &&
      typeof error.detail === "object" &&
      "message" in error.detail
    ) {
      const message = (error.detail as { message?: unknown }).message;
      if (typeof message === "string") return message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Tente novamente mais tarde.";
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.email ||
      !formData.phone ||
      !formData.password
    ) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos",
        variant: "destructive",
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Senhas diferentes",
        description: "As senhas não coincidem",
        variant: "destructive",
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Senha fraca",
        description: "A senha deve ter pelo menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      await authService.register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
      });

      navigate("/register/plans", {
        state: { email: formData.email, name: formData.name },
      });
    } catch (error: unknown) {
      console.error("Erro no registro:", error);
      toast({
        title: "Erro ao criar conta",
        description: getRegisterErrorDescription(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7)
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const benefits = [
    "Dashboard unificado de todas as plataformas",
    "Google Analytics, Meta Ads e Google Ads",
    "Relatórios inteligentes com IA",
    "Alertas personalizados de performance",
    "Suporte dedicado e treinamento",
  ];

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Section - Benefits */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#2869C1] via-[#2869C1] to-[#1d5299] p-12 flex-col justify-center relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 space-y-10">
          {/* Logo + Title */}
          <div className="space-y-6">
            <img
              src="/logoSidebar.png"
              alt="Painel do E-Commerce"
              className="h-16 brightness-0 invert"
            />
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium">
                <Sparkles className="h-4 w-4" />
                Comece gratuitamente
              </div>
              <h1 className="text-4xl font-bold text-white leading-tight">
                Escale seu
                <br />
                e-commerce hoje
              </h1>
              <p className="text-white/80 text-lg max-w-md">
                Milhares de lojistas já transformaram seus resultados com o
                Painel do E-Commerce. Junte-se a eles e comece agora.
              </p>
            </div>
          </div>

          {/* Benefits List */}
          <div className="space-y-3">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 text-white">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <span className="text-white/90">{benefit}</span>
              </div>
            ))}
          </div>

          {/* Testimonial */}
          <div className="p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
            <p className="text-white/90 italic mb-4">
              "O Painel do E-Commerce mudou completamente nossa visão sobre os
              dados. Agora tomamos decisões baseadas em informações reais."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#9CCB3B] flex items-center justify-center text-white font-semibold">
                RF
              </div>
              <div>
                <p className="text-white font-medium">Roberto Ferreira</p>
                <p className="text-white/60 text-sm">Lojista desde 2024</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-white/60 text-sm pt-8">
            © {new Date().getFullYear()} Painel do E-Commerce. Todos os direitos
            reservados.
          </div>
        </div>
      </div>

      {/* Right Section - Register Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="/logoSidebar.png"
              alt="Painel do E-Commerce"
              className="h-20 mx-auto"
            />
          </div>

          {/* Header */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-slate-900">
              Criar sua conta
            </h2>
            <p className="mt-2 text-slate-600">
              Comece a transformar seus dados gratuitamente
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleRegister} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700">
                Nome completo
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="name"
                  placeholder="Como devemos te chamar?"
                  className="pl-11 h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-[#2869C1] text-slate-900"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">
                E-mail
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  className="pl-11 h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-[#2869C1] text-slate-900"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-slate-700">
                Telefone
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="phone"
                  placeholder="(11) 99999-9999"
                  className="pl-11 h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-[#2869C1] text-slate-900"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      phone: formatPhone(e.target.value),
                    }))
                  }
                  disabled={isLoading}
                  required
                  maxLength={15}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">
                  Senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Mín. 6 caracteres"
                    className="pl-11 pr-11 h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-[#2869C1] text-slate-900"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    disabled={isLoading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-slate-700">
                  Confirmar
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Repita a senha"
                    className="pl-11 h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-[#2869C1] text-slate-900"
                    value={formData.confirmPassword}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        confirmPassword: e.target.value,
                      }))
                    }
                    disabled={isLoading}
                    required
                  />
                </div>
              </div>
            </div>

            <p className="text-xs text-slate-500">
              Ao criar sua conta, você concorda com nossos{" "}
              <a
                href="https://www.paineldoecommerce.com.br/termos-de-uso/"
                target="_blank"
                rel="noreferrer"
                className="text-[#2869C1] hover:underline"
              >
                Termos de Uso
              </a>{" "}
              e{" "}
              <a
                href="https://www.paineldoecommerce.com.br/politica-de-privacidade/"
                target="_blank"
                rel="noreferrer"
                className="text-[#2869C1] hover:underline"
              >
                Política de Privacidade
              </a>
              .
            </p>

            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold bg-[#9CCB3B] hover:bg-[#8bba34]"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Criando conta...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  Continuar
                  <ArrowRight className="h-4 w-4" />
                </div>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-500">ou</span>
            </div>
          </div>

          {/* Login Link */}
          <div className="text-center space-y-4">
            <p className="text-slate-600">
              Já tem uma conta?{" "}
              <Link
                to="/login"
                className="font-semibold text-[#2869C1] hover:underline"
              >
                Fazer login
              </Link>
            </p>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={() => navigate("/login")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar ao Login
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
